import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { REQUESTS } from "@/constants/testIds";
import { openMailtoFromTemplate } from "@/lib/emailTemplates";
import { toast, confirmToast, softDeleteWithUndo } from "@/lib/notify";
import {
  Plus,
  Pencil,
  Trash2,
  CheckCircle2,
  XCircle,
  AlertCircle,
  ClipboardList,
  Mail,
  X,
} from "lucide-react";

const TYPE_LABEL = {
  salon: "Salón",
  reunion: "Reunión",
  alabanza: "Alabanza",
  taller: "Taller",
};
const TYPES = ["salon", "reunion", "alabanza", "taller"];
const STATUS_LABEL = {
  pendiente: "Pendiente",
  aprobado: "Aprobado",
  rechazado: "Rechazado",
};

const STATUS_PILL = {
  pendiente: "border-amber-400 bg-amber-50 text-amber-800",
  aprobado: "border-emerald-400 bg-emerald-50 text-emerald-800",
  rechazado: "border-red-400 bg-red-50 text-red-800",
};

const today = () => new Date().toISOString().slice(0, 10);

function openMailtoForDecision(item, action, adminNotes) {
  const estadoMayus = action === "aprobado" ? "APROBADA" : "RECHAZADA";
  openMailtoFromTemplate(
    "request_decision",
    item.created_by,
    {
      tipo: TYPE_LABEL[item.type] || item.type,
      titulo: item.title,
      fecha: item.date,
      hora: item.time || "",
      lugar: item.location || "",
      estado: action,
      estado_mayus: estadoMayus,
      notas_admin: adminNotes || "(sin notas)",
    },
    {
      context_label: `Solicitud ${estadoMayus}: ${item.title}`,
      context_id: item.id,
    },
  );
}

function openMailtoForNewRequest(adminEmails, item) {
  const to = adminEmails.join(",");
  openMailtoFromTemplate(
    "request_new",
    to,
    {
      tipo: TYPE_LABEL[item.type] || item.type,
      titulo: item.title,
      fecha: item.date,
      hora: item.time || "",
      lugar: item.location || "",
      asistentes: item.expected_attendance || "",
      presupuesto: item.budget ? `$${Number(item.budget).toFixed(2)}` : "",
      responsable: item.responsible || "",
      motivo: item.reason || "",
      solicitante: item.created_by,
    },
    {
      context_label: `Nueva solicitud: ${item.title}`,
      context_id: item.id,
    },
  );
}

export default function Solicitudes() {
  const { user } = useAuth();
  const isAdmin = user?.role === "administrador";

  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ pendiente: 0, aprobado: 0, rechazado: 0, total: 0 });
  const [filterStatus, setFilterStatus] = useState("");
  const [filterType, setFilterType] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [decisionItem, setDecisionItem] = useState(null);
  const [decisionAction, setDecisionAction] = useState("aprobado");
  const [decisionNotes, setDecisionNotes] = useState("");
  const [adminEmails, setAdminEmails] = useState([]);

  const load = async () => {
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterType) params.type_ = filterType;
      const [r, s] = await Promise.all([
        api.get("/requests", { params }),
        api.get("/requests/stats/summary"),
      ]);
      setRows(r.data);
      setStats(s.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando solicitudes");
    }
  };

  useEffect(() => {
    queueMicrotask(load);
  }, [filterStatus, filterType]);

  // Load admin emails once for notification feature (D)
  useEffect(() => {
    api
      .get("/agenda/users")
      .then((r) => {
        const admins = (r.data || [])
          .filter((u) => u.role === "administrador")
          .map((u) => u.email);
        setAdminEmails(admins);
      })
      .catch(() => {});
  }, []);

  const openCreate = () => {
    setEditing(null);
    setShowForm(true);
  };
  const openEdit = (row) => {
    setEditing(row);
    setShowForm(true);
  };

  const remove = (row) => {
    // Optimistic UI removal
    setRows((prev) => prev.filter((r) => r.id !== row.id));
    setStats((prev) => {
      const next = { ...prev };
      if (prev[row.status] != null) next[row.status] = Math.max(0, prev[row.status] - 1);
      if (prev.total != null) next.total = Math.max(0, prev.total - 1);
      return next;
    });
    softDeleteWithUndo({
      message: "Solicitud eliminada",
      description: row.title,
      onUndo: () => load(),
      onDelete: async () => {
        try {
          await api.delete(`/requests/${row.id}`);
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Error eliminando — restaurando");
          load();
        }
      },
    });
  };

  const openDecision = (row, action) => {
    setDecisionItem(row);
    setDecisionAction(action);
    setDecisionNotes("");
  };
  const submitDecision = async () => {
    try {
      await api.post(`/requests/${decisionItem.id}/decision`, {
        status: decisionAction,
        admin_notes: decisionNotes,
      });
      const decisionItemSnapshot = decisionItem;
      const decisionActionSnapshot = decisionAction;
      const decisionNotesSnapshot = decisionNotes;
      setDecisionItem(null);
      load();
      // Offer to send email notification to creator (non-blocking confirm toast)
      const wantEmail = await confirmToast(
        `Solicitud ${decisionActionSnapshot === "aprobado" ? "aprobada" : "rechazada"}.`,
        {
          description: `¿Notificar a ${decisionItemSnapshot.created_by} por email?`,
          confirmLabel: "Enviar email",
          cancelLabel: "No",
        },
      );
      if (wantEmail) {
        openMailtoForDecision(decisionItemSnapshot, decisionActionSnapshot, decisionNotesSnapshot);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al revisar");
    }
  };

  return (
    <div data-testid={REQUESTS.page} className="p-8 max-w-7xl mx-auto">
      <Header isAdmin={isAdmin} stats={stats} onAdd={openCreate} />

      <div className="mb-4 flex flex-wrap gap-3 items-center">
        <select
          data-testid={REQUESTS.filterStatus}
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="border border-[#d9cfb8] bg-white px-3 py-1.5 text-[13px] rounded"
        >
          <option value="">Todos los estados</option>
          <option value="pendiente">Pendientes</option>
          <option value="aprobado">Aprobados</option>
          <option value="rechazado">Rechazados</option>
        </select>
        <select
          data-testid={REQUESTS.filterType}
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="border border-[#d9cfb8] bg-white px-3 py-1.5 text-[13px] rounded"
        >
          <option value="">Todos los tipos</option>
          {TYPES.map((t) => (
            <option key={t} value={t}>{TYPE_LABEL[t]}</option>
          ))}
        </select>
      </div>

      <Table
        rows={rows}
        isAdmin={isAdmin}
        currentEmail={user?.email}
        onEdit={openEdit}
        onDelete={remove}
        onDecision={openDecision}
      />

      {showForm && (
        <RequestForm
          initial={editing}
          onClose={() => setShowForm(false)}
          onSaved={(saved, isNew) => {
            setShowForm(false);
            load();
            // Offer to notify admin by email on NEW requests (feature D)
            if (isNew && saved && adminEmails.length > 0) {
              // Avoid notifying yourself if admin is creating their own request
              const recipients = adminEmails.filter((em) => em !== user?.email);
              if (recipients.length > 0) {
                confirmToast("Solicitud creada.", {
                  description: "¿Notificar al administrador por email?",
                  confirmLabel: "Enviar email",
                  cancelLabel: "No",
                }).then((wantEmail) => {
                  if (wantEmail) openMailtoForNewRequest(recipients, saved);
                });
              }
            }
          }}
        />
      )}

      {decisionItem && (
        <DecisionModal
          item={decisionItem}
          action={decisionAction}
          notes={decisionNotes}
          setNotes={setDecisionNotes}
          onCancel={() => setDecisionItem(null)}
          onSubmit={submitDecision}
        />
      )}
    </div>
  );
}

function Header({ isAdmin, stats, onAdd }) {
  return (
    <div className="mb-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-3xl text-[#1c1a14] flex items-center gap-2">
            <ClipboardList className="text-[#c8a04a]" /> Solicitudes
          </h1>
          <p className="text-[13px] text-[#8b7e5e] mt-1">
            {isAdmin
              ? "Revisa y aprueba/rechaza las solicitudes de salón, reuniones, alabanza y talleres."
              : "Solicita permisos para salón, reuniones, alabanza o talleres."}
          </p>
        </div>
        <button
          data-testid={REQUESTS.addButton}
          onClick={onAdd}
          className="inline-flex items-center gap-2 rounded border border-[#c8a04a] bg-[#c8a04a] px-3 py-1.5 text-[12px] uppercase tracking-widest text-[#1c1a14] hover:bg-[#b8902a]"
        >
          <Plus size={14} /> Nueva solicitud
        </button>
      </div>
      <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Total" value={stats.total} status="total" />
        <Stat label="Pendientes" value={stats.pendiente} status="pendiente" />
        <Stat label="Aprobadas" value={stats.aprobado} status="aprobado" />
        <Stat label="Rechazadas" value={stats.rechazado} status="rechazado" />
      </div>
    </div>
  );
}

function Stat({ label, value, status }) {
  return (
    <div
      data-testid={REQUESTS.statBadge(status)}
      className="rounded border border-[#d9cfb8] bg-white px-4 py-3"
    >
      <div className="text-[10px] uppercase tracking-widest text-[#8b7e5e]">{label}</div>
      <div className="font-serif text-2xl text-[#1c1a14]">{value}</div>
    </div>
  );
}

function Table({ rows, isAdmin, currentEmail, onEdit, onDelete, onDecision }) {
  if (!rows.length) {
    return (
      <div className="rounded border border-dashed border-[#d9cfb8] bg-white p-10 text-center text-[#8b7e5e]">
        No hay solicitudes registradas.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto rounded border border-[#d9cfb8] bg-white">
      <table data-testid={REQUESTS.table} className="w-full text-[13px]">
        <thead className="bg-[#0f1a2c] text-[#e7c879]">
          <tr>
            <Th>Tipo</Th>
            <Th>Título</Th>
            <Th>Fecha</Th>
            <Th>Lugar</Th>
            <Th>Asist.</Th>
            <Th>Presupuesto</Th>
            <Th>Solicitante</Th>
            <Th>Estado</Th>
            <Th>Acciones</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const mine = r.created_by === currentEmail;
            return (
              <tr
                key={r.id}
                data-testid={REQUESTS.row(r.id)}
                className="border-t border-[#ebe2c9] hover:bg-[#fbf7ee]"
              >
                <Td>{TYPE_LABEL[r.type] || r.type}</Td>
                <Td className="font-medium text-[#1c1a14]">{r.title}</Td>
                <Td>{r.date}{r.time ? ` · ${r.time}` : ""}</Td>
                <Td>{r.location || "—"}</Td>
                <Td>{r.expected_attendance || 0}</Td>
                <Td>${Number(r.budget || 0).toFixed(2)}</Td>
                <Td className="text-[11px] text-[#8b7e5e]">{r.created_by}</Td>
                <Td>
                  <span className={`inline-block text-[10px] uppercase tracking-widest border rounded px-2 py-0.5 ${STATUS_PILL[r.status]}`}>
                    {STATUS_LABEL[r.status]}
                  </span>
                </Td>
                <Td>
                  <div className="flex gap-1 flex-wrap">
                    {isAdmin && r.status === "pendiente" && (
                      <>
                        <button
                          data-testid={REQUESTS.approveButton(r.id)}
                          onClick={() => onDecision(r, "aprobado")}
                          title="Aprobar"
                          className="text-emerald-600 hover:text-emerald-800 p-1 rounded"
                        >
                          <CheckCircle2 size={16} />
                        </button>
                        <button
                          data-testid={REQUESTS.rejectButton(r.id)}
                          onClick={() => onDecision(r, "rechazado")}
                          title="Rechazar"
                          className="text-red-600 hover:text-red-800 p-1 rounded"
                        >
                          <XCircle size={16} />
                        </button>
                      </>
                    )}
                    {(mine || isAdmin) && r.status === "pendiente" && (
                      <button
                        data-testid={REQUESTS.editButton(r.id)}
                        onClick={() => onEdit(r)}
                        title="Editar"
                        className="text-[#5d5849] hover:text-[#1c1a14] p-1"
                      >
                        <Pencil size={16} />
                      </button>
                    )}
                    {isAdmin && r.status !== "pendiente" && (
                      <button
                        data-testid={`requests-email-${r.id}`}
                        onClick={() => openMailtoForDecision(r, r.status, r.admin_notes)}
                        title="Reenviar notificación por email"
                        className="text-sky-600 hover:text-sky-800 p-1"
                      >
                        <Mail size={16} />
                      </button>
                    )}
                    {(mine || isAdmin) && (
                      <button
                        data-testid={REQUESTS.deleteButton(r.id)}
                        onClick={() => onDelete(r)}
                        title="Eliminar"
                        className="text-red-500 hover:text-red-700 p-1"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </Td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const Th = ({ children }) => (
  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest font-normal">{children}</th>
);
const Td = ({ children, className = "" }) => (
  <td className={`px-3 py-2 ${className}`}>{children}</td>
);

function RequestForm({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(
    initial || {
      type: "salon",
      title: "",
      date: today(),
      time: "",
      location: "",
      reason: "",
      expected_attendance: 0,
      budget: 0,
      materials: "",
      responsible: "",
      notes: "",
    },
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const body = {
        type: form.type,
        title: form.title,
        date: form.date,
        time: form.time || "",
        location: form.location || "",
        reason: form.reason || "",
        expected_attendance: Number(form.expected_attendance) || 0,
        budget: Number(form.budget) || 0,
        materials: form.materials || "",
        responsible: form.responsible || "",
        notes: form.notes || "",
      };
      let saved;
      if (initial) {
        const r = await api.put(`/requests/${initial.id}`, body);
        saved = r.data;
      } else {
        const r = await api.post("/requests", body);
        saved = r.data;
      }
      onSaved(saved, !initial);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={initial ? "Editar solicitud" : "Nueva solicitud"} onClose={onClose} testId={REQUESTS.formModal}>
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Tipo *">
            <select
              data-testid={REQUESTS.typeSelect}
              value={form.type}
              onChange={(e) => update("type", e.target.value)}
              className="w-full border border-[#d9cfb8] bg-white px-3 py-2 text-[13px] rounded"
              required
            >
              {TYPES.map((t) => (
                <option key={t} value={t}>{TYPE_LABEL[t]}</option>
              ))}
            </select>
          </Field>
          <Field label="Título / Nombre del evento *">
            <input
              data-testid={REQUESTS.titleInput}
              value={form.title}
              onChange={(e) => update("title", e.target.value)}
              className="w-full border border-[#d9cfb8] bg-white px-3 py-2 text-[13px] rounded"
              required
            />
          </Field>
          <Field label="Fecha *">
            <input
              data-testid={REQUESTS.dateInput}
              type="date"
              value={form.date}
              onChange={(e) => update("date", e.target.value)}
              className="w-full border border-[#d9cfb8] bg-white px-3 py-2 text-[13px] rounded"
              required
            />
          </Field>
          <Field label="Hora">
            <input
              data-testid={REQUESTS.timeInput}
              type="time"
              value={form.time}
              onChange={(e) => update("time", e.target.value)}
              className="w-full border border-[#d9cfb8] bg-white px-3 py-2 text-[13px] rounded"
            />
          </Field>
          <Field label="Salón / Lugar">
            <input
              data-testid={REQUESTS.locationInput}
              value={form.location}
              onChange={(e) => update("location", e.target.value)}
              className="w-full border border-[#d9cfb8] bg-white px-3 py-2 text-[13px] rounded"
            />
          </Field>
          <Field label="Responsable">
            <input
              data-testid={REQUESTS.responsibleInput}
              value={form.responsible}
              onChange={(e) => update("responsible", e.target.value)}
              className="w-full border border-[#d9cfb8] bg-white px-3 py-2 text-[13px] rounded"
            />
          </Field>
          <Field label="Asistentes esperados">
            <input
              data-testid={REQUESTS.attendanceInput}
              type="number"
              min="0"
              value={form.expected_attendance}
              onChange={(e) => update("expected_attendance", e.target.value)}
              className="w-full border border-[#d9cfb8] bg-white px-3 py-2 text-[13px] rounded"
            />
          </Field>
          <Field label="Presupuesto ($)">
            <input
              data-testid={REQUESTS.budgetInput}
              type="number"
              min="0"
              step="0.01"
              value={form.budget}
              onChange={(e) => update("budget", e.target.value)}
              className="w-full border border-[#d9cfb8] bg-white px-3 py-2 text-[13px] rounded"
            />
          </Field>
        </div>
        <Field label="Motivo / Descripción">
          <textarea
            data-testid={REQUESTS.reasonInput}
            value={form.reason}
            onChange={(e) => update("reason", e.target.value)}
            rows={2}
            className="w-full border border-[#d9cfb8] bg-white px-3 py-2 text-[13px] rounded"
          />
        </Field>
        <Field label="Materiales necesarios">
          <textarea
            data-testid={REQUESTS.materialsInput}
            value={form.materials}
            onChange={(e) => update("materials", e.target.value)}
            rows={2}
            className="w-full border border-[#d9cfb8] bg-white px-3 py-2 text-[13px] rounded"
          />
        </Field>
        <Field label="Notas adicionales">
          <textarea
            data-testid={REQUESTS.notesInput}
            value={form.notes}
            onChange={(e) => update("notes", e.target.value)}
            rows={2}
            className="w-full border border-[#d9cfb8] bg-white px-3 py-2 text-[13px] rounded"
          />
        </Field>

        {err && (
          <div data-testid={REQUESTS.errorMsg} className="flex items-center gap-2 rounded border border-red-300 bg-red-50 px-3 py-2 text-[12px] text-red-700">
            <AlertCircle size={14} /> {err}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            data-testid={REQUESTS.cancelButton}
            onClick={onClose}
            disabled={busy}
            className="rounded border border-[#d9cfb8] px-3 py-1.5 text-[12px] uppercase tracking-widest text-[#5b5240] hover:bg-[#fbf7ee]"
          >
            Cancelar
          </button>
          <button
            type="submit"
            data-testid={REQUESTS.saveButton}
            disabled={busy}
            className="rounded border border-[#c8a04a] bg-[#c8a04a] px-3 py-1.5 text-[12px] uppercase tracking-widest text-[#1c1a14] hover:bg-[#b8902a] disabled:opacity-50"
          >
            {busy ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function DecisionModal({ item, action, notes, setNotes, onCancel, onSubmit }) {
  return (
    <Modal
      title={action === "aprobado" ? "Aprobar solicitud" : "Rechazar solicitud"}
      onClose={onCancel}
      testId={REQUESTS.decisionModal}
    >
      <p className="text-[13px] text-[#5d5849] mb-3">
        <strong>{item.title}</strong> ({TYPE_LABEL[item.type]}) — {item.date}
      </p>
      <Field label="Notas para el solicitante (opcional)">
        <textarea
          data-testid={REQUESTS.decisionNotes}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          className="w-full border border-[#d9cfb8] bg-white px-3 py-2 text-[13px] rounded"
          placeholder={action === "aprobado" ? "Comentarios o instrucciones..." : "Motivo del rechazo..."}
        />
      </Field>
      <div className="flex justify-end gap-2 pt-3">
        <button
          onClick={onCancel}
          className="rounded border border-[#d9cfb8] px-3 py-1.5 text-[12px] uppercase tracking-widest text-[#5b5240] hover:bg-[#fbf7ee]"
        >
          Cancelar
        </button>
        <button
          data-testid={action === "aprobado" ? REQUESTS.decisionApprove : REQUESTS.decisionReject}
          onClick={onSubmit}
          className={`rounded border px-3 py-1.5 text-[12px] uppercase tracking-widest ${
            action === "aprobado"
              ? "border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700"
              : "border-red-600 bg-red-600 text-white hover:bg-red-700"
          }`}
        >
          Confirmar {action === "aprobado" ? "aprobación" : "rechazo"}
        </button>
      </div>
    </Modal>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-widest text-[#8b7e5e] mb-1">{label}</span>
      {children}
    </label>
  );
}

function Modal({ title, onClose, testId, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        data-testid={testId}
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded border border-[#d9cfb8] bg-white p-6 shadow-xl"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-lg text-[#1c1a14]">{title}</h3>
          <button onClick={onClose} className="text-[#8b7e5e] hover:text-[#1c1a14]">
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
